/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../../store';
import {EAdasProgramsLoadingStateTypes, TCoverageRequirementsLoadingState} from '../AdasPrograms/slice';
import {IComponent} from "../../../containers/components/Global/Dialog/GlobalDialog";

export const enum EBluelineReportStatusMapRequestTypes {
  UPDATE = 'update',
  ONLY_ADD_NEW = 'onlyAddNew'
}

interface ILanding {
  editVehicleProgramDialogProps: any
}

interface IDialogState {
  isAddVehicleProgramVersionDialogOpen: boolean
  isDeleteVehicleProgramVersionDialogOpen: boolean
  isOverrideVersionDataWarningDialogOpen: boolean
  overrideDialogData: any
  isConfigSensorReferenceFrameDialogOpen: boolean
  isReportDialogOpen: boolean
  component: IComponent | null
}

interface ISensingSystemPerformance {
  currentReportData: any
  loadingState: TCoverageRequirementsLoadingState
  devStageToRedlineIdToReportStatus: any
  reportGenerationStatusObjectMap: any
}

interface IState {
  dialogState: IDialogState
  landing: ILanding
  itemDetails: any
  sensingSystemPerformance: ISensingSystemPerformance
}

const initialState: IState = {
  dialogState: {
    isAddVehicleProgramVersionDialogOpen: false,
    isDeleteVehicleProgramVersionDialogOpen: false,
    isOverrideVersionDataWarningDialogOpen: false,
    overrideDialogData: null,
    isConfigSensorReferenceFrameDialogOpen: false,
    isReportDialogOpen: false,
    component: null,
  },
  landing: {
    editVehicleProgramDialogProps: null
  },
  itemDetails: {
    developmentStagesTree: null,
    mainData: null,
    selectedDevelopmentStageData: null,
    isLoadingDevelopmentStageData: false,
    modifiedSensors: null,
    isSaved: true,
    coordinates: {
      systemName: "NX",
    },
    refreshAfterNewDevelopmentStageAdded: 0,
  },
  sensingSystemPerformance: {
    currentReportData: null,
    loadingState: {
      [EAdasProgramsLoadingStateTypes.EDIT_REPORT]: {
        isLoading: false,
        data: null
      },
    },
    devStageToRedlineIdToReportStatus: {},
    reportGenerationStatusObjectMap: {},
  }
} as IState

const vehicleProgramsDetails = createSlice({
  name: 'vehicleprogramsdetails',
  initialState,
  reducers: {

    setIsAddVehicleProgramVersionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isAddVehicleProgramVersionDialogOpen = action.payload
    },
    setIsDeleteVehicleProgramVersionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isDeleteVehicleProgramVersionDialogOpen = action.payload
    },
    setIsOverrideVersionDataWarningDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isOverrideVersionDataWarningDialogOpen = action.payload
    },
    setOverrideDialogData: (state, action: PayloadAction<any>) => {
      state.dialogState.overrideDialogData = action.payload
    },
    setIsConfigSensorReferenceFrameDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isConfigSensorReferenceFrameDialogOpen = action.payload
    },
    setIsReportDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isReportDialogOpen = action.payload
    },
    setDialogComponent: (state, action: PayloadAction<IComponent>) => {
      state.dialogState.component = action.payload
    },
    setLandingEditVehicleProgramDialogProps: (state, action: PayloadAction<any>) => {
      state.landing.editVehicleProgramDialogProps = action.payload
    },
    setDevelopmentStagesTree: (state, action: PayloadAction<any>) => {
      state.itemDetails.developmentStagesTree = action.payload
    },
    setItemDetailsMainData: (state, action: PayloadAction<any>) => {
      state.itemDetails.mainData = action.payload
    },
    setVehicleProgramsSelectedDevelopmentStageData: (state, action: PayloadAction<any>) => {
      state.itemDetails.selectedDevelopmentStageData = action.payload
    },
    setIsLoadingDevelopmentStageData: (state, action: PayloadAction<boolean>) => {
      state.itemDetails.isLoadingDevelopmentStageData = action.payload
    },
    setVehicleProgramsModifiedSensors: (state, action: PayloadAction<any>) => {
      const {data, type} = action.payload
      if (type == 'update') {
        state.itemDetails.modifiedSensors = {...state.itemDetails.modifiedSensors, ...data}
      } else {
        state.itemDetails.modifiedSensors = data
      }
      state.itemDetails.isSaved = false
    },
    setVehicleProgramsIsSaved: (state, action: PayloadAction<boolean>) => {
      state.itemDetails.isSaved = action.payload
    },

    setCurrentReportData: (state, action: PayloadAction<any>) => void (state.sensingSystemPerformance.currentReportData = action.payload),
    setSensingSystemPerformanceReportLoadingState: (state, action: PayloadAction<TCoverageRequirementsLoadingState>) => {
      state.sensingSystemPerformance.loadingState = {...state.sensingSystemPerformance.loadingState, ...action.payload}
    },
    setSensingSystemPerformanceReportLoadingStateIsLoading: (state, action: PayloadAction<TCoverageRequirementsLoadingState>) => {
      state.sensingSystemPerformance.loadingState = {...state.sensingSystemPerformance.loadingState, ...action.payload}
    },
    updateReportStatusMap: (state, action: PayloadAction<any>) => {
      // take the current report status map and update it with the new data (a partial map that will override the existing data)
      const {data, requestType} = action.payload

      switch (requestType) {
        case EBluelineReportStatusMapRequestTypes.UPDATE: {
          const {developmentStageId, adasProgramId, redlineDatasetIdToStatusMap} = data
          if (!state.sensingSystemPerformance.devStageToRedlineIdToReportStatus.hasOwnProperty(developmentStageId)) {
            state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId] = {}
          }
          if (!state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId].hasOwnProperty(adasProgramId)) {
            state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId] = {}
          }
          state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId] = {
            ...state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId],
            ...redlineDatasetIdToStatusMap
          }
          // console.log('updated report status map', state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId], redlineDatasetIdToStatusMap)
          break
        }
        case EBluelineReportStatusMapRequestTypes.ONLY_ADD_NEW: {
          const {developmentStageId, adasProgramId, redlineDatasetIdToStatusMap} = data
          if (!state.sensingSystemPerformance.devStageToRedlineIdToReportStatus.hasOwnProperty(developmentStageId)) {
            state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId] = {}
          }
          if (!state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId].hasOwnProperty(adasProgramId)) {
            state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId] = {}
          }
          // update the report status map with redlineDatasetIdToStatusMap, but don't override the existing data. So, for keys that already exist, don't update them
          state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId] = {
            ...state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId],
            ...Object.keys(redlineDatasetIdToStatusMap).reduce((acc: any, key) => {
              if (!state.sensingSystemPerformance.devStageToRedlineIdToReportStatus[developmentStageId][adasProgramId].hasOwnProperty(key)) {
                acc[key] = redlineDatasetIdToStatusMap[key]
              }
              return acc
            }, {})
          }
        }
      }
    },
    setReportGenerationStatusMap: (state, action: PayloadAction<any>) => {
      const {data, requestType} = action.payload
      switch (requestType) {
        case 'update': {
          const {reportGenerationStatusObjectMap} = data
          state.sensingSystemPerformance.reportGenerationStatusObjectMap = {...state.sensingSystemPerformance.reportGenerationStatusObjectMap, ...reportGenerationStatusObjectMap}
          break
        }
        case 'overwrite': {
          state.sensingSystemPerformance.reportGenerationStatusObjectMap = {...data}
          break
        }
      }
    },
    setCoordinateSystemName: (state, action: PayloadAction<any>) => {
      state.itemDetails.coordinates.systemName = action.payload
    },
    resetVehicleProgramsState: () => initialState,

    setTriggerRefreshAfterNewAdasProgramAdded: (state) => {
      state.itemDetails.refreshAfterNewDevelopmentStageAdded += 1
    },
    setDevStageIdToData: (state, action: PayloadAction<any>) => {
      const devStageIdToData = action.payload
      state.itemDetails.devStageIdToData = devStageIdToData
    }
  },
})

const selectDialogState = (state: RootState) => state.vehicleProgramsDetails.dialogState

const selectVehicleProgramsLandingState = (state: RootState) => state.vehicleProgramsDetails.landing
const selectVehicleProgramsItemDetailsState = (state: RootState) => state.vehicleProgramsDetails.itemDetails
const selectVehicleProgramsDevelopmentStagesTree = (state: RootState) => state.vehicleProgramsDetails.itemDetails.developmentStagesTree
const selectVehicleProgramsMainData = (state: RootState) => state.vehicleProgramsDetails.itemDetails.mainData
const selectVehicleProgramsSelectedDevelopmentStageData = (state: RootState) => state.vehicleProgramsDetails.itemDetails.selectedDevelopmentStageData
const selectIsLoadingDevelopmentStageData = (state: RootState) => state.vehicleProgramsDetails.itemDetails.isLoadingDevelopmentStageData
const selectVehicleProgramsSensingSystemPerformanceState = (state: RootState) => state.vehicleProgramsDetails.sensingSystemPerformance
const selectCurrentReportData = (state: RootState) => state.vehicleProgramsDetails.sensingSystemPerformance.currentReportData
const selectSensingSystemPerformanceReportLoadingState = (state: RootState, type: EAdasProgramsLoadingStateTypes) => state.vehicleProgramsDetails.sensingSystemPerformance.loadingState?.[type]
const selectSensingSystemPerformanceReportLoadingStateIsLoading = (state: RootState, type: EAdasProgramsLoadingStateTypes) => state.vehicleProgramsDetails.sensingSystemPerformance.loadingState?.[type]?.isLoading ?? false
const selectCoordinateSystemName = (state: RootState) => state.vehicleProgramsDetails.itemDetails.coordinates.systemName

const selectTriggerRefreshAfterNewAdasProgramAdded = (state: RootState) => state.vehicleProgramsDetails.itemDetails.refreshAfterNewDevelopmentStageAdded


const {actions, reducer} = vehicleProgramsDetails


export const {
  setIsAddVehicleProgramVersionDialogOpen, setIsDeleteVehicleProgramVersionDialogOpen,
  setIsOverrideVersionDataWarningDialogOpen, setOverrideDialogData, setIsConfigSensorReferenceFrameDialogOpen,
  setIsReportDialogOpen, setDialogComponent,

  setLandingEditVehicleProgramDialogProps,
  setDevelopmentStagesTree,
  setItemDetailsMainData,
  setVehicleProgramsSelectedDevelopmentStageData,
  setIsLoadingDevelopmentStageData,
  setVehicleProgramsModifiedSensors,
  setVehicleProgramsIsSaved,

  setCurrentReportData,
  setSensingSystemPerformanceReportLoadingState,
  setSensingSystemPerformanceReportLoadingStateIsLoading,
  updateReportStatusMap,
  setReportGenerationStatusMap,

  setCoordinateSystemName,
  resetVehicleProgramsState,

  setTriggerRefreshAfterNewAdasProgramAdded,
} = actions
export {
  selectDialogState,
  selectVehicleProgramsLandingState,
  selectVehicleProgramsItemDetailsState,
  selectVehicleProgramsDevelopmentStagesTree,
  selectVehicleProgramsMainData,
  selectVehicleProgramsSelectedDevelopmentStageData,
  selectIsLoadingDevelopmentStageData,

  selectVehicleProgramsSensingSystemPerformanceState,
  selectCurrentReportData,
  selectSensingSystemPerformanceReportLoadingState,
  selectSensingSystemPerformanceReportLoadingStateIsLoading,

  selectCoordinateSystemName,

  selectTriggerRefreshAfterNewAdasProgramAdded,
}
export default reducer
